import { inject } from '@angular/core';
import { HttpEvent, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { MyCMIntegrationService } from '../services';
import { EnvironmentService } from '../services/environment.service';
import { debugLog } from '../utils/debug-logger';

function resolveDefaultContentType(req: HttpRequest<unknown>, env: EnvironmentService): string {
  const keycloakTokenUrl = `${env.keycloak.url}/realms/${env.keycloak.realm}/protocol/openid-connect/token`;
  return req.url === keycloakTokenUrl
    ? 'application/x-www-form-urlencoded'
    : 'application/json';
}

function withDefaultContentTypeIfMissing(
  headers: HttpRequest<unknown>['headers'],
  defaultContentType: string,
) {
  return headers.has('Content-Type')
    ? headers
    : headers.set('Content-Type', defaultContentType);
}

export function myCMTokenInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  // Never force Content-Type on multipart/form-data requests — the browser must
  // set it automatically with the correct boundary parameter.
  const isMultipart = req.body instanceof FormData;
  const env = inject(EnvironmentService);
  const defaultContentType = resolveDefaultContentType(req, env);

  const headers = req.headers;
  if (headers.has('x-skip-token')) {
    const cloned = isMultipart
      ? req.clone({ headers: req.headers.delete('x-skip-token') })
      : req.clone({
          headers: withDefaultContentTypeIfMissing(
            req.headers.delete('x-skip-token'),
            defaultContentType,
          ),
        });
    return next(cloned);
  }

  const myCMIntegrationService = inject(MyCMIntegrationService);
  const detail = myCMIntegrationService.myCMConfig$.getValue();
  // Use the top-level token field (required in MyCMConfig).
  // userInfo is optional and may not be present in all MyCM deployments.
  const token = detail?.token;

  debugLog('myCMTokenInterceptor', 'request', {
    url: req.url,
    hasConfig: !!detail,
    hasToken: !!token,
  });

  if (!token) {
    if (isMultipart) return next(req);
    return next(
      req.clone({
        headers: withDefaultContentTypeIfMissing(
          req.headers,
          defaultContentType,
        ),
      }),
    );
  }

  const authReq = isMultipart
    ? req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) })
    : req.clone({
        headers: withDefaultContentTypeIfMissing(
          req.headers.set('Authorization', `Bearer ${token}`),
          defaultContentType,
        ),
      });

  return next(authReq).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse && [401, 0].includes(error.status)) {
        myCMIntegrationService.postMessage('ethico-check-authentication');
      }

      return throwError(() => error);
    }),
  );
}
